/* eslint-disable arrow-body-style */

import { useMemo } from 'react';
import useRequest from '@gp/hooks/useRequest';
import { getStudioList } from '@gp/services/project';
import { fetchCommonConditions } from '@gp/services/filter';
import { initOptions, sortByChars } from '@gp/utils/common';
import {
  renameOfProjectGroupOptions,
  getCascadeProjectDatas,
  getCascadeProjectValues,
} from '@gp/utils/project';
import {
  CUSTOMIZED_OTHERS,
  PROGRESS_REFLECT,
  STATIC_MILESTONES,
  DEFAULT_STAGE_VALUE_OVERVIEW,
} from '@gp/constants/const';
import { KEYS_MAPPING, getFilterInterWords } from '@gp/constants/field';
import { GROUP_KEY } from '@gp/constants/select';
import { STAGE_OPT } from '@gp/pages/Workspace/Product/components/GameOverview/utils/constant';
import type { IRecord } from '@gp/types/global';

interface IPipelineProjectListFilterOptionsAndValuesMapping {
  options: IRecord<IGPSelectOptionWithGroup[]>;
  values: IRecord<string[]>;
}

const isJSONStructure = (str: string) => {
  return !!(str?.includes('{') && str?.includes('}'));
};

const sortValue = (a: any, b: any, isObject?: boolean) => {
  let nameA = a?.toUpperCase();
  let nameB = b?.toUpperCase();
  // 数据结构可能是json字符串对象
  if (isObject && isJSONStructure(nameA) && isJSONStructure(nameB)) {
    try {
      nameA = Object.values(JSON.parse(nameA) || {})[0] || nameA;
      nameB = Object.values(JSON.parse(nameB) || {})[0] || nameB;
    } catch (err) {}
  }
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
};

const getValidValues = (list1?: string[], list2?: string[]) => {
  return list1?.filter((item1) => list2?.includes(item1)) || [];
};

const getSortedBusinessOwners = (list?: string[] | null) => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.sort((a, b) => sortValue(a, b, true));
};

const initOptionsForStudio = (
  list?: { id: string; name: string; }[] | null,
): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(list) || !list.length) return [];
  // a-z排序
  const sortedList = sortByChars('name', list);

  return sortedList.map((item) => {
    const { id, name } = item || {};
    return { value: id, text: name, groupKey: GROUP_KEY, label: name };
  });
};

const fetchFilterConditions = async (): Promise<IPipelineProjectListFilterConditionsVO> => {
  const filterConditions: IPipelineProjectListFilterConditionsVO = {
    // pteam: [],
    // business_owner: [],
    // studio: [],
    platform: [],
    milestone: [],
    next_milestone: [],
    publisher: [],
    status: [],
    stage: [],
    form: [],
    project_id: [],
  };

  const commonCondParams = {
    pteam_bo_studio_project: { project_auth: true },
    platform: [],
    current_milestone: [],
    next_milestone: [],
    publisher: [],
    dps_status: [],
    stage: [],
    group: [],
  };

  try {
    const filterConditionsResList = await Promise.allSettled([
      // getSelectsFilter(),
      fetchCommonConditions(commonCondParams),
      getStudioList({ type: 1 }),
    ]);

    for (let i = 0; i < filterConditionsResList.length; i++) {
      const filterConditionsResItem = filterConditionsResList[i];
      // @ts-ignore
      const { status, value } = filterConditionsResItem || {};

      if (status !== 'fulfilled') continue;
      // 处理通用字段的筛选器options数据
      if (i === 0) {
        Object.keys(filterConditions).forEach((key) => {
          if (key === 'project_id') {
            const cascadeProjectoptions = getCascadeProjectDatas(
              value?.pteam_bo_studio_project?.pteam_list,
              'project_auth',
            );
            filterConditions[key] = cascadeProjectoptions || [];
          } else {
            // @ts-ignore
            filterConditions[key] = value?.[getFilterInterWords(key)] || [];
          }
        });
      }
      // 处理studio字段的筛选器options数据
      if (i === 1) {
        // @ts-ignore
        filterConditions.studio = value || [];
      }
    }
  } catch (exception) {}
  return filterConditions;
};

const addMilestoneAndNextMilestoneData = (
  result?: IPipelineProjectListFilterOptionsAndValuesMapping | null,
): void => {
  if (!result) return;
  const milestoneOptions = [...STATIC_MILESTONES, CUSTOMIZED_OTHERS].map((item) => {
    return { ...item, groupKey: GROUP_KEY, label: item?.text || '' };
  });
  const milestoneValues = milestoneOptions.map((option) => option?.value);
  result.options.milestone = milestoneOptions;
  result.values.milestone = milestoneValues;
  result.options.next_milestone = milestoneOptions;
  result.values.next_milestone = milestoneValues;
};

export default function useOptionsAndValues() {
  const { loading: filterConditionsLoading, data: filterConditions } =
    useRequest(fetchFilterConditions);
  const { options, values, defaultValues } =
    useMemo<IPipelineProjectListFilterOptionsAndValuesMapping>(() => {
      if (filterConditionsLoading) return { options: {}, values: {}, defaultValues: {} };
      if (!filterConditions) return { options: {}, values: {}, defaultValues: {} };
      const result: IPipelineProjectListFilterOptionsAndValuesMapping = {
        options: {},
        values: {},
        defaultValues: {},
      };

      Object.entries(filterConditions).forEach(([key, value]) => {
        switch (key) {
          // case KEYS_MAPPING.BUSINESS_OWNER: {
          //   result.options[key] = initOptions(getSortedBusinessOwners(value));
          //   break;
          // }
          // case KEYS_MAPPING.STUDIO: {
          //   result.options[key] = initOptionsForStudio(value);
          //   break;
          // }
          // case KEYS_MAPPING.P_TEAM:
          case KEYS_MAPPING.PLATFORM:
          case KEYS_MAPPING.PUBLISHER: {
            result.options[key] = sortByChars('text', initOptions(value));
            break;
          }
          case KEYS_MAPPING.MILESTONE:
          case KEYS_MAPPING.NEXT_MILESTONE: {
            result.options[key] = [...STATIC_MILESTONES, CUSTOMIZED_OTHERS].map((item) => {
              return { ...item, groupKey: GROUP_KEY, label: item?.text || '' };
            });
            break;
          }
          case KEYS_MAPPING.STATUS: {
            // status需要将other特殊处理为'0'
            const statusOptions = initOptions(value).map((item) => {
              const { value } = item || {};
              // 如果是OTHER，值就取0
              const valueTmp = value === 'OTHER' ? '0' : String(PROGRESS_REFLECT[value]);
              return { ...item, value: valueTmp };
            });
            result.options[key] = sortByChars('text', statusOptions);
            break;
          }
          case KEYS_MAPPING.STAGE: {
            result.options[key] = STAGE_OPT.filter((item) => {
              return value
                ?.map((valueItem: string) => valueItem?.toLowerCase())
                ?.includes(item?.value?.toLowerCase());
            }).map((item) => {
              return { ...item, groupKey: GROUP_KEY, label: item?.text || '' };
            });
            break;
          }
          case KEYS_MAPPING.GROUP: {
            result.options[key] = sortByChars(
              'text',
              renameOfProjectGroupOptions(initOptions(value)),
            );
            break;
          }
          case KEYS_MAPPING.CASCADE_PROJECT: {
            result.options[key] = value;
            break;
          }
          default: {
            result.options[key] = initOptions(value);
          }
        }

        const optionValues = result.options[key].map((option) => option?.value);

        if (key === KEYS_MAPPING.STAGE) {
          result.values[key] = getValidValues(DEFAULT_STAGE_VALUE_OVERVIEW, optionValues);
        } else if (key === KEYS_MAPPING.CASCADE_PROJECT) {
          result.values[key] = getCascadeProjectValues(result.options[key], true);
          result.defaultValues[key] = getCascadeProjectValues(result.options[key], false);
        } else {
          result.values[key] = optionValues;
        }
      });

      addMilestoneAndNextMilestoneData(result);

      return result;
    }, [filterConditionsLoading, filterConditions]);

  return { loading: filterConditionsLoading, options, values, defaultValues };
}
